import type { Activators, SensorInstance, SensorProps } from '@dnd-kit/core';
import type { Coordinates } from '@dnd-kit/utilities';
import { InputResources, type TabDragScope } from './TabDragScope';

type Options = { scope: TabDragScope };
const constraint = { distance: 8 };

/** 仅使用dnd-kit公开Sensor契约，不继承或访问内置Sensor的私有成员。 */
export class TabPointerSensor implements SensorInstance {
  autoScrollEnabled = true;
  static activators: Activators<Options> = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent }, { scope }) =>
        scope.active && nativeEvent.isPrimary && nativeEvent.button === 0,
    },
  ];

  private resources: InputResources;
  private clickGuard: InputResources | null = null;
  private closed = false;
  private started = false;
  private target: Element | null = null;
  private captured = false;
  private pointerId: number;
  private initial: Coordinates;

  constructor(private props: SensorProps<Options>) {
    const event = props.event as PointerEvent;
    this.pointerId = event.pointerId;
    this.initial = { x: event.clientX, y: event.clientY };
    this.resources = new InputResources(props.options.scope);
    this.resources.add(() => {
      this.closed = true;
      this.clickGuard?.dispose();
      this.clickGuard = null;
      this.releasePointer();
    });
    if (this.closed) return;
    if (!(event.target instanceof Element)) {
      this.resources.dispose();
      return;
    }
    this.target = event.target;
    const doc = this.target.ownerDocument;
    this.resources.listen(doc, 'pointermove', (move) => this.move(move), {
      passive: false,
    });
    this.resources.listen(doc, 'pointerup', (up) => {
      if (up.pointerId === this.pointerId) this.finish('end');
    });
    this.resources.listen(doc, 'pointercancel', (cancel) => {
      if (cancel.pointerId === this.pointerId) this.finish('cancel');
    });
    this.resources.listen(this.target, 'lostpointercapture', (lost) => {
      if (lost.pointerId === this.pointerId && !this.closed)
        this.finish('cancel', true);
    });
    this.resources.listen(doc, 'keydown', (key) => {
      if (key.code === 'Escape') this.finish('cancel', true);
    });
    this.resources.listen(doc, 'visibilitychange', () => this.finish('cancel', true));
    if (doc.defaultView)
      this.resources.listen(doc.defaultView, 'resize', () =>
        this.finish('cancel', true),
      );
    const node = props.activeNode.node.current ?? this.target;
    this.resources.listen(node, 'dragstart', (nativeDrag) =>
      nativeDrag.preventDefault(),
    );
    props.onPending(props.active, constraint, this.initial);
  }

  private move(event: PointerEvent) {
    if (event.pointerId !== this.pointerId) return;
    if (event.buttons === 0) {
      this.finish('cancel');
      return;
    }
    const coordinates = { x: event.clientX, y: event.clientY };
    if (!this.started) {
      const delta = {
        x: coordinates.x - this.initial.x,
        y: coordinates.y - this.initial.y,
      };
      if (Math.hypot(delta.x, delta.y) <= constraint.distance) {
        this.props.onPending(this.props.active, constraint, this.initial, delta);
        return;
      }
      this.start();
      return;
    }
    event.preventDefault();
    this.props.onMove(coordinates);
  }

  private start() {
    if (!this.target?.isConnected) {
      this.finish('cancel');
      return;
    }
    try {
      // 达到8px阈值才捕获，真正松手仍走onEnd正常换序。
      this.target.setPointerCapture(this.pointerId);
      this.captured = true;
    } catch {
      // 原生指针已失效时取消当前输入，不能留下半启动的监听。
      this.finish('cancel');
      return;
    }
    this.started = true;
    const guard = new InputResources(this.props.options.scope);
    this.clickGuard = guard;
    const node = this.props.activeNode.node.current ?? this.target;
    guard.listen(
      node,
      'click',
      (event) => {
        if (event.detail === 0) return;
        if ('pointerId' in event && event.pointerId !== this.pointerId) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { capture: true },
    );
    const doc = this.target.ownerDocument;
    const clearSelection = () => doc.getSelection()?.removeAllRanges();
    clearSelection();
    this.resources.listen(doc, 'selectionchange', clearSelection);
    this.props.onStart(this.initial);
  }

  private finish(reason: 'end' | 'cancel', waitForRelease = false) {
    if (this.closed) return;
    const guard = this.clickGuard;
    this.clickGuard = null;
    this.resources.dispose();
    // 正常松手后只拦截该标签的尾随click；此计时器仍归标签栏scope所有。
    if (guard && waitForRelease && this.target) {
      // Escape取消排序时鼠标可能仍按住：只保留该标签的点击保护直到真实松手。
      // 等待期间不再发排序回调；标签栏卸载仍立即清除这些scope所属资源。
      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        guard.defer(() => guard.dispose(), 50);
      };
      const doc = this.target.ownerDocument;
      guard.listen(doc, 'pointerup', (event) => {
        if (event.pointerId === this.pointerId) release();
      });
      guard.listen(doc, 'pointermove', (event) => {
        if (event.pointerId === this.pointerId && event.buttons === 0) release();
      });
      guard.listen(doc, 'pointercancel', (event) => {
        if (event.pointerId === this.pointerId) guard.dispose();
      });
      guard.listen(doc, 'pointerdown', (event) => {
        if (event.pointerId === this.pointerId) guard.dispose();
      });
    } else if (reason === 'end') guard?.defer(() => guard.dispose(), 50);
    else guard?.dispose();
    if (!this.props.options.scope.active) return;
    if (!this.started) this.props.onAbort(this.props.active);
    if (!this.props.options.scope.active) return;
    if (reason === 'end') this.props.onEnd();
    else this.props.onCancel();
  }

  private releasePointer() {
    if (!this.captured || !this.target) return;
    this.captured = false;
    if (this.target.hasPointerCapture(this.pointerId))
      this.target.releasePointerCapture(this.pointerId);
    const focused = this.target.ownerDocument.activeElement;
    // 与原capture修复一致：只清理本次鼠标关联的非当前标签焦点。
    if (
      focused instanceof HTMLElement &&
      focused.matches('[role="tab"][aria-selected="false"]') &&
      (focused.contains(this.target) || this.target.contains(focused))
    )
      focused.blur();
  }
}
